/**
 * Plain-English lines for a civilisation's traits and unique unit (ADR-0062), written
 * from the numbers in traits.json and units.json so the words cannot drift from what
 * the engine does. Used by the new-game screen and the Rivals screen.
 */

import type { CivilizationDef, Content, TraitDef, UnitDef } from '../../content/schema/index.js';

function nameList(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Group a discount table by percentage: "Libraries, … cost 25% less". */
function discountLines(table: Record<string, number>, nameOf: (key: string) => string): string[] {
  const byPercent = new Map<number, string[]>();
  for (const [key, percent] of Object.entries(table)) byPercent.set(percent, [...(byPercent.get(percent) ?? []), nameOf(key)]);
  return [...byPercent.entries()].sort((a, b) => b[0] - a[0]).map(([percent, names]) => `${nameList(names)} cost ${percent}% less`);
}

export function describeTrait(content: Content, trait: TraitDef): string[] {
  const e = trait.effects;
  const lines = [
    ...discountLines(e.buildingDiscount, (key) => content.buildings.get(content.buildings.id(key)).name),
    ...discountLines(e.unitDiscount, (key) => content.units.get(content.units.id(key)).name),
  ];
  if (e.sciencePercent > 0) lines.push(`+${e.sciencePercent}% science in every city`);
  if (e.goldPerCity > 0) lines.push(`+${e.goldPerCity} gold in every city`);
  if (e.centreFood > 0) lines.push(`+${e.centreFood} food on every city's own tile`);
  if (e.promotionPercent > 0) lines.push(`Soldiers are promoted ${e.promotionPercent}% more often`);
  if (e.seaMovement > 0) lines.push(`Ships move ${e.seaMovement} further`);
  if (e.anarchyTurnsLess > 0) lines.push(`Revolutions are ${e.anarchyTurnsLess} turn${e.anarchyTurnsLess === 1 ? '' : 's'} shorter`);
  return lines;
}

const stats = (u: UnitDef): string => `${u.attack}/${u.defence}/${u.movement}`;

/** "War Elephant (replaces Knight) — 5/3/1 against 4/3/2", attack/defence/moves. */
export function describeUnique(content: Content, civ: CivilizationDef): { name: string; replaces: string; line: string } | null {
  if (civ.uniqueUnitId === -1) return null;
  const unit = content.units.get(civ.uniqueUnitId);
  const base = content.units.get(unit.replacesId);
  const cost = unit.cost !== base.cost ? `, costs ${unit.cost} not ${base.cost}` : '';
  return {
    name: unit.name,
    replaces: base.name,
    line: `${stats(unit)} against the ${base.name}'s ${stats(base)} (attack/defence/moves)${cost}`,
  };
}

/** Trait names, "Religious · Agricultural". */
export function traitNames(content: Content, civ: CivilizationDef): string {
  return civ.traitIds.map((id) => content.traits.get(id).name).join(' · ');
}
